import type { BaseCanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ElementType, ValueFormat } from '@/typings/designStudio/sourceElementTypes';
import type { CanvasEx } from '@/typings/canvas';

/** Per element type operations the pattern handlers delegate to (texts: TextService, images: imagePatternReplicas) */
export interface PatternReplicaService {
    elementType: ElementType;
    /** Replicas built/synced per batch, yielding to the browser between them. Omitted: all at once (texts) */
    batchSize?: number;
    /** With batchSize: yield only once this much work piled up since the last yield. Omitted: after every batch */
    batchTimeBudgetMs?: number;
    buildPatternReplica: (
        canvas: CanvasEx,
        origin: PatternElement,
        cell: PatternCell,
        options: {
            uuid?: string;
            left: number;
            top: number;
            zIndex?: number;
            editMode?: boolean;
            transform?: OriginTransform;
        }
    ) => PatternElement | Promise<PatternElement>;
    repositionPatternReplica: (
        origin: PatternElement,
        replica: PatternElement,
        position: ReplicaPosition,
        resize: boolean
    ) => void;
    syncPatternReplica: (
        canvas: CanvasEx,
        origin: PatternElement,
        replica: PatternElement,
        position: ReplicaPosition
    ) => Promise<void>;
}

/** An elementsLinks entry as ProductService keeps it; pattern ones link an origin (source) to a replica (target) */
export interface PatternLink {
    sourceId: string;
    targetId: string;
    targetType?: ElementType;
    valueFormat: ValueFormat;
}

export interface PatternProductService {
    addPatternElementsLink: (originUuid: string, replicaUuid: string, elementType: ElementType) => void;
    removePatternElementsLinks: (originUuid: string) => void;
    removePatternElementsLinkByReplica: (replicaUuid: string) => void;
    getPatternReplicaLinks: (originUuid: string) => PatternLink[];
}

export interface CreatePatternHandlersParams {
    getCanvas: () => CanvasEx;
    $scope: { editMode?: boolean };
    ProductService: PatternProductService;
    getReplicaService: (origin: PatternElement) => PatternReplicaService;
}

export interface PatternProps {
    pattern?: boolean;
    patternConfig?: PatternConfig | null;
    isPatternReplica?: boolean;
    patternOriginUuid?: string;
    patternCell?: PatternCell;
}

/** Only what texts and image placeholders share: the per type replica services cast to their own element */
export type PatternElement = BaseCanvasElement & PatternProps;

export interface PatternConfig {
    spacingX: number;
    spacingY: number;
    offsetX: number;
    offsetY: number;
    rows: number;
    cols: number;
}

/** Pixels shared by several patterns whatever each origin's size (copy distance, odd row/col shift); null if mixed */
export interface SharedRepeat {
    repeatX: number | null;
    repeatY: number | null;
    shiftX: number | null;
    shiftY: number | null;
}

export type SharedRepeatChange = Partial<{ [K in keyof SharedRepeat]: number }>;

export const PATTERN_DEFAULTS: PatternConfig = {
    spacingX: 0,
    spacingY: 0,
    offsetX: 0,
    offsetY: 0,
    rows: 1,
    cols: 1
};

export interface PatternCell {
    row: number;
    col: number;
}

/** The origin's on-canvas angle/scale while a rotated or scaled selection holds it: its own are relative to it */
export interface OriginTransform {
    angle: number;
    scaleX: number;
    scaleY: number;
}

/** `transform` only inside a transformed selection: otherwise replicas take the origin's own angle/scale */
export interface ReplicaPosition {
    left: number;
    top: number;
    transform?: OriginTransform;
}

export interface CellPosition extends PatternCell, ReplicaPosition {}
